import React, { forwardRef, useRef, useState, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __spreadArray(to, from) {
  for (var i = 0, il = from.length, j = to.length; i < il; i++, j++) {
    to[j] = from[i];
  }

  return to;
}

var hexGen = function hexGen(len) {
  if (len === void 0) {
    len = 12;
  }

  var maxlen = 8;
  var min = Math.pow(16, Math.min(len, maxlen) - 1);
  var max = Math.pow(16, Math.min(len, maxlen)) - 1;
  var n = Math.floor(Math.random() * (max - min + 1)) + min;
  var r = n.toString(16);

  while (r.length < len) {
    r = r + hexGen(len - maxlen);
  }

  return r;
};

var DEFAULT_SCOPE = 'stack';
var SCOPE_KEY = 'factoryStack';

if (typeof window !== 'undefined') {
  if (!window[SCOPE_KEY]) {
    window[SCOPE_KEY] = {};
  }
}

var registerContainer = function registerContainer(scope, ref) {
  window[SCOPE_KEY][scope] = ref;
  return ref;
};

var unregisterContainer = function unregisterContainer(scope) {
  delete window[SCOPE_KEY][scope];
};

var getContainer = function getContainer(scope) {
  return window[SCOPE_KEY][scope || DEFAULT_SCOPE];
};

var InstanceContainer = function InstanceContainer(props, ref) {
  var _a = (props || {}).scope,
      scope = _a === void 0 ? DEFAULT_SCOPE : _a;
  var propsRef = useRef(props);

  var _b = useState({}),
      instances = _b[0],
      setInstances = _b[1];

  var _c = useState([]),
      hashStack = _c[0],
      setHashStack = _c[1];

  var resolve = useCallback(function (hash, v) {
    var _a;

    return (_a = instances === null || instances === void 0 ? void 0 : instances[hash]) === null || _a === void 0 ? void 0 : _a.resolve(v);
  }, [instances]);
  var resolveAll = useCallback(function (v) {
    return Object.values(instances).forEach(function (i) {
      return i.resolve(v);
    });
  }, [instances]);
  var reject = useCallback(function (hash, r) {
    var _a;

    return (_a = instances === null || instances === void 0 ? void 0 : instances[hash]) === null || _a === void 0 ? void 0 : _a.reject(r);
  }, [instances]);
  var rejectAll = useCallback(function (r) {
    return Object.values(instances).forEach(function (i) {
      return i.reject(r);
    });
  }, [instances]);
  var hasInstance = useCallback(function (hash) {
    return !!hashStack.find(function (id) {
      return id === hash;
    });
  }, [hashStack]);
  var getInstance = useCallback(function (hash) {
    return instances === null || instances === void 0 ? void 0 : instances[hash];
  }, [instances]);

  var remove = function remove(hash, options) {
    var _a;

    setHashStack(function (stack) {
      return stack.filter(function (s) {
        return s !== hash;
      });
    });
    setTimeout(function () {
      setInstances(function (instances) {
        var _a = instances,
            _b = hash;
        _a[_b];

        var omitHash = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);

        return omitHash;
      });
    }, options === null || options === void 0 ? void 0 : options.exitTimeout);
    (_a = props.onRemove) === null || _a === void 0 ? void 0 : _a.call(props, hash);
  };

  var create = function create(Component, options, instanceProps) {
    if (options === void 0) {
      options = {};
    }

    return new Promise(function (res, rej) {
      var hash = (instanceProps === null || instanceProps === void 0 ? void 0 : instanceProps.instanceId) || hexGen();
      var _a = propsRef.current,
          enterTimeout = _a.enterTimeout,
          exitTimeout = _a.exitTimeout,
          isAppendIntances = _a.isAppendIntances,
          onResolve = _a.onResolve,
          onReject = _a.onReject;

      var instanceOptions = _assign({
        enterTimeout: enterTimeout,
        exitTimeout: exitTimeout,
        instanceId: hash
      }, options);

      var instance = _assign({
        Component: Component,
        props: _assign(_assign({}, options), instanceProps),
        resolve: function resolve(v) {
          removeRef.current(hash, instanceOptions);
          res(v);
          onResolve === null || onResolve === void 0 ? void 0 : onResolve(v, hash);
        },
        reject: function reject(r) {
          removeRef.current(hash, instanceOptions);
          rej(r);
          onReject === null || onReject === void 0 ? void 0 : onReject(r, hash);
        }
      }, instanceOptions);

      setInstances(function (instances) {
        var _a, _b;

        return isAppendIntances ? _assign(_assign({}, instances), (_a = {}, _a[hash] = instance, _a)) : _assign((_b = {}, _b[hash] = instance, _b), instances);
      });
      setTimeout(function () {
        var _a, _b;

        setHashStack(function (stack) {
          return __spreadArray(__spreadArray([], stack), [hash]);
        });
        (_b = (_a = propsRef.current).onOpen) === null || _b === void 0 ? void 0 : _b.call(_a, hash, instance);
      }, instanceOptions.enterTimeout);
    });
  };

  var removeRef = useRef(remove);
  var createRef = useRef(create);
  useEffect(function () {
    propsRef.current = props;
    removeRef.current = remove;
    createRef.current = create;
  });
  useImperativeHandle(ref, function () {
    return {
      create: createRef.current,
      resolve: resolve,
      reject: reject,
      resolveAll: resolveAll,
      rejectAll: rejectAll,
      hasInstance: hasInstance,
      getInstance: getInstance
    };
  });
  useEffect(function () {
    registerContainer(scope, {
      create: createRef.current,
      resolve: resolve,
      reject: reject,
      resolveAll: resolveAll,
      rejectAll: rejectAll,
      hasInstance: hasInstance,
      getInstance: getInstance
    });
    return function () {
      return unregisterContainer(scope);
    };
  }, [scope]);
  var mapKeys = useMemo(function () {
    var keys = Object.keys(instances);
    return keys.map(function (key) {
      var _a = instances[key],
          Component = _a.Component,
          props = _a.props,
          resolve = _a.resolve,
          reject = _a.reject;
      var isOpen = !!hashStack.find(function (h) {
        return h === key;
      });
      return React.createElement(Component, _assign({}, props, {
        key: key,
        isOpen: isOpen,
        onReject: reject,
        onResolve: resolve,

        /** @deprecated **/
        close: resolve,

        /** @deprecated **/
        open: isOpen
      }));
    });
  }, [instances, hashStack]);
  return React.createElement(React.Fragment, null, mapKeys);
};

var Container = forwardRef(InstanceContainer);
Container.defaultProps = {
  exitTimeout: 500,
  enterTimeout: 50
};
/** @deprecated */

var createModal = function createModal(Component, options) {
  return function (props) {
    return getContainer(options === null || options === void 0 ? void 0 : options.scope).create(Component, options, props);
  };
};

var create = function create(Component, options) {
  return function (props) {
    return getContainer(options === null || options === void 0 ? void 0 : options.scope).create(Component, options, props);
  };
};

export default Container;
export { Container, DEFAULT_SCOPE, SCOPE_KEY, create, createModal, getContainer, hexGen, registerContainer, unregisterContainer };